import React, {
  ChangeEvent,
  ReactNode,
  ReactElement,
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EditOutlined from '@mui/icons-material/EditOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import MetaTag from 'components/meta';
import styles from 'components/medicalHistory/page.module.scss';
import Button from 'components/common/button';
import InputText from 'components/common/InputText';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { isEmptyString } from 'utils/helper';
import { US_STATES, USStateType } from 'lib/usStateOptions';
import Label from 'components/common/label';
import { useSelector } from 'react-redux';
import { NullablePatientDTO } from 'types/patients';
import { RootState } from 'store/store';
import { size } from 'lodash';
import {
  NPISearchItemDTO,
  ProviderContextDTO,
  ProviderEntryDTO,
} from 'artifacts/dtos';
import type { GetApiIntakeAppProviderSearchExternalProviderParams } from 'artifacts/dtos/getApiIntakeAppProviderSearchExternalProviderParams';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import dayjs, { Dayjs } from 'dayjs';
import { useSearchExternalProviders } from 'hooks/useApi';
import { SearchOutlined } from '@mui/icons-material';

const MuiDatePicker = dynamic(() => import('../common/datePicker'), { ssr: false });

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

type ProviderType = 'individual' | 'organization';
type ProviderSearchInput = {
  firstName: string;
  lastName: string;
  organizationName: string;
  state: string;
};
type ProviderSearchHandler = (input: ProviderSearchInput) => void;
type ProviderTypeConfig = {
  label: string;
  icon: ReactElement;
  isReady: boolean;
  searchDisabledReason: string;
  search: ProviderSearchHandler;
  fields: ReactNode;
};

function mapNPISearchItemToProviderEntry(item: NPISearchItemDTO): ProviderEntryDTO {
  return {
    type: item.isOrganization ? 'Organization' : 'Individual',
    npi: {
      number: item.npi ?? null,
      taxonomyCode: item.taxonomyCode ?? null,
      type: item.npiType ?? null,
    },
    state: item.state ?? null,
    firstName: item.firstName ?? null,
    middleName: item.middleName ?? null,
    lastName: item.lastName ?? null,
    credential: item.credential ?? null,
    organizationName: item.organizationName ?? null,
    formattedNames: item.formattedNames,
  };
}

function legacyFlatToProviderContext(
  v: Record<string, string | undefined | null>,
): ProviderContextDTO {
  const stateVal = String(v.contactState ?? v.state ?? '').trim();
  const firstName = String(v.firstName ?? '').trim();
  const lastName = String(v.lastName ?? '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return {
    providerEntry: {
      type: 'Individual',
      firstName: firstName || null,
      lastName: lastName || null,
      state: stateVal || null,
      formattedNames: {
        fullName: fullName || null,
      },
    },
    firstSeenOn: v.firstSeenOn as string | null | undefined,
    lastSeenOn: v.dateLastSeen && dayjs(v.dateLastSeen).isValid()
      ? String(v.dateLastSeen)
      : null,
  };
}

function normalizeHydratedPcp(raw: unknown): ProviderContextDTO | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  if (o.providerEntry && typeof o.providerEntry === 'object') {
    return {
      providerEntry: o.providerEntry as ProviderEntryDTO,
      lastSeenOn: (o.lastSeenOn as string | null | undefined) ?? null,
      firstSeenOn: (o.firstSeenOn as string | null | undefined) ?? undefined,
    };
  }
  const fn = o.firstName as string | undefined;
  const ln = o.lastName as string | undefined;
  const st = (o.state ?? o.contactState) as string | undefined;
  if (isEmptyString(fn) && isEmptyString(ln) && isEmptyString(st)) {
    return null;
  }
  return legacyFlatToProviderContext({
    firstName: fn,
    lastName: ln,
    contactState: o.contactState as string | undefined,
    state: st,
    dateLastSeen: o.dateLastSeen as string | undefined,
  });
}

function hasDispatchablePcpContext(ctx: ProviderContextDTO): boolean {
  if (ctx.providerEntry && Object.keys(ctx.providerEntry).length > 0) {
    return true;
  }
  return false;
}

function getUsStateAutocompleteLabel(option: USStateType): string {
  return `${option.name} - ${option.abbreviation}`;
}

function renderAutocompleteTextField(params: TextFieldProps) {
  return <TextField {...params} />;
}

function getNpiSearchResultKey(item: NPISearchItemDTO): string | null | undefined {
  const parts = [
    item.npi,
    item.state,
    item.firstName,
    item.lastName,
    item.organizationName,
    item.city,
    item.taxonomyCode,
    item.addressLine1,
    item.postalCode,
  ].filter((x) => x != null && String(x) !== '');
  if (parts.length > 0) {
    return parts.join('|');
  }
  return item.formattedNames?.fullNameWithCredentials;
}

type PhysicianSearchResultCardProps = {
  item: NPISearchItemDTO;
  onSelect: (item: NPISearchItemDTO) => void;
};

function PhysicianSearchResultCard({ item, onSelect }: PhysicianSearchResultCardProps) {
  const name = item.formattedNames?.fullNameWithCredentials;
  const npi = item.npi ?? '—';
  const addressLine1 = item.addressLine1 ?? null;
  const addressLine2 = item.addressLine2 ?? null;
  const cityStateZip = [item.city, item.state, item.postalCode]
    .filter((x) => !isEmptyString(x ?? ''))
    .join(', ');
  const handleButtonClick = useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);
  return (
    <Box
      component="button"
      type="button"
      onClick={handleButtonClick}
      sx={{
        width: '100%',
        textAlign: 'left',
        p: 1.5,
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        '&:last-child': { mb: 0 },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} component="div">
        {name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        NPI
        {' '}
        {npi}
        {!isEmptyString(item.taxonomy) ? ` · ${item.taxonomy}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {compact([addressLine1, addressLine2, cityStateZip]).join(', ') || '—'}
      </Typography>
    </Box>
  );
}

type SelectedPhysicianCardProps = {
  entry: ProviderEntryDTO;
  icon: ReactNode;
  onEdit: () => void;
  onRemove: () => void;
};

function SelectedPhysicianCard({
  entry,
  icon,
  onEdit,
  onRemove,
}: SelectedPhysicianCardProps) {
  const name = entry.formattedNames?.fullNameWithCredentials;
  const state = entry.state ?? '—';
  const npi = entry.npi?.number ?? '—';
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} component="div">
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            NPI
            {' '}
            {npi}
            {' · '}
            {state}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <IconButton
            type="button"
            size="small"
            aria-label="Edit primary care physician"
            onClick={onEdit}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
          <IconButton
            type="button"
            size="small"
            aria-label="Remove primary care physician"
            onClick={onRemove}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

function PrimaryCarePhysician({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchLastName, setSearchLastName] = useState('');
  const [searchOrganizationName, setSearchOrganizationName] = useState('');
  const [searchStateAbbrev, setSearchStateAbbrev] = useState('');
  const [searchMode, setSearchMode] = useState<ProviderType>('individual');
  const [submittedSearchParams, setSubmittedSearchParams] = useState<
  GetApiIntakeAppProviderSearchExternalProviderParams | null
  >(null);
  const [searchValidationError, setSearchValidationError] = useState('');
  const [pcpContext, setPcpContext] = useState<ProviderContextDTO>({});
  const [lastSeenOnDraft, setLastSeenOnDraft] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [doesNotHavePrimaryCarePhysician, setDoesNotHavePrimaryCarePhysician] = useState(false);

  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const [state, dispatch] = usePersistMHReducer();

  const shouldRunSearch = submittedSearchParams !== null;
  const {
    data: searchResults = [],
    isFetching: isSearchFetching,
    isError: isSearchError,
    error: searchError,
  } = useSearchExternalProviders(submittedSearchParams ?? {}, shouldRunSearch);

  useEffect(() => {
    if (!state) {
      return;
    }
    const primaryCarePhysician = state.find(
      (page: StateTypeLocalStorage) => page.name === actionPage,
    );
    const primaryCarePhysicianValue = primaryCarePhysician?.value;
    if (size(primaryCarePhysicianValue)) {
      const normalized = normalizeHydratedPcp(primaryCarePhysicianValue);
      if (normalized) {
        setPcpContext(normalized);
        setLastSeenOnDraft(normalized.lastSeenOn ?? null);
        setIsEditing(false);
        setDoesNotHavePrimaryCarePhysician(false);
      } else if (typeof primaryCarePhysicianValue === 'object') {
        setDoesNotHavePrimaryCarePhysician(
          (primaryCarePhysicianValue as Record<string, unknown>).hasNoPrimaryCarePhysician === true,
        );
      }
    } else {
      const fromPatient = normalizeHydratedPcp(patient?.primaryCarePhysician as unknown);
      if (fromPatient) {
        setPcpContext(fromPatient);
        setLastSeenOnDraft(fromPatient.lastSeenOn ?? null);
        setDoesNotHavePrimaryCarePhysician(false);
      } else {
        setDoesNotHavePrimaryCarePhysician(patient?.hasNoPrimaryCarePhysician === true);
      }
      const patientAddressState = patient?.address?.state;
      if (!isEmptyString(patientAddressState ?? '')) {
        setSearchStateAbbrev(patientAddressState!);
      }
    }
  }, []);

  const selectedSearchState = useMemo(() => US_STATES.find(
    (region) => (
      region.name === searchStateAbbrev?.trim()
      || region.abbreviation === searchStateAbbrev?.trim()
    ),
  ) ?? { name: '', abbreviation: '' }, [searchStateAbbrev]);

  const hasSelectedProvider = hasDispatchablePcpContext(pcpContext);
  const showSearchForm = !hasSelectedProvider || isEditing;
  const showSelectedCard = hasSelectedProvider && !isEditing;
  const showDatePicker = hasSelectedProvider;

  let searchButtonLabel = 'Search';
  if (isSearchFetching) {
    searchButtonLabel = 'Searching…';
  }

  const lastSeenPickerValue = useMemo(() => (
    isEmptyString(pcpContext.lastSeenOn ?? '')
      ? undefined
      : dayjs(pcpContext.lastSeenOn)
  ), [pcpContext.lastSeenOn]);

  const onSearchStateChange = useCallback((_e: unknown, value: USStateType | null) => {
    if (!isNil(value)) {
      setSearchStateAbbrev(value?.abbreviation ?? '');
    } else {
      setSearchStateAbbrev('');
    }
  }, []);

  const handleSearchModeChange = useCallback((mode: ProviderType) => {
    setSearchMode(mode);
    setSubmittedSearchParams(null);
    setSearchValidationError('');
  }, []);

  const handleSearchAgain = useCallback(() => {
    setSubmittedSearchParams(null);
    setSearchValidationError('');
  }, []);

  const handleSelectSearchResult = useCallback((item: NPISearchItemDTO) => {
    const entry = mapNPISearchItemToProviderEntry(item);
    setPcpContext({
      providerEntry: entry,
      lastSeenOn: lastSeenOnDraft,
    });
    setIsEditing(false);
    setSubmittedSearchParams(null);
    setSearchValidationError('');
  }, [lastSeenOnDraft]);

  const handleEdit = useCallback(() => {
    setLastSeenOnDraft(pcpContext.lastSeenOn ?? null);
    setPcpContext({});
    setIsEditing(true);
    setSubmittedSearchParams(null);
    setSearchValidationError('');
  }, [pcpContext.lastSeenOn]);

  const handleRemove = useCallback(() => {
    setPcpContext({});
    setLastSeenOnDraft(null);
    setIsEditing(false);
    setSubmittedSearchParams(null);
    setSearchValidationError('');
  }, []);

  const handleLastSeenChange = useCallback((value: Dayjs | null) => {
    const next = value?.toString() ?? '';
    setLastSeenOnDraft(isEmptyString(next) ? null : next);
    setPcpContext((prev) => ({
      ...prev,
      lastSeenOn: isEmptyString(next) ? null : next,
    }));
  }, []);

  const handleSearchFirstNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length <= 30) {
      setSearchFirstName(value.trimStart());
    }
  }, []);

  const handleSearchLastNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length <= 30) {
      setSearchLastName(value.trimStart());
    }
  }, []);

  const handleSearchOrganizationNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value.length <= 80) {
        setSearchOrganizationName(value.trimStart());
      }
    },
    [],
  );

  const providerTypeConfig = useMemo<Record<ProviderType, ProviderTypeConfig>>(
    () => ({
      individual: {
        label: 'Individual',
        icon: (
          <PersonOutlineOutlined
            aria-label="Individual"
            titleAccess="Individual"
            className="text-populate-green mt-0.5"
          />
        ),
        isReady: (
          (!isEmptyString(searchFirstName.trim()) || !isEmptyString(searchLastName.trim()))
          && !isEmptyString(searchStateAbbrev.trim())
        ),
        searchDisabledReason: 'Enter first name + state or last name + state to search',
        search: (input) => {
          if (isEmptyString(input.firstName)
            && isEmptyString(input.lastName)
            && isEmptyString(input.state)) {
            setSearchValidationError('Enter at least first name, last name, or state to search.');
            setSubmittedSearchParams(null);
            return;
          }
          setSubmittedSearchParams({
            firstName: input.firstName || undefined,
            lastName: input.lastName || undefined,
            state: input.state || undefined,
            npiType: 'NPI-1 Individual',
          });
        },
        fields: (
          <>
            <Label>First Name</Label>
            <InputText
              onChange={handleSearchFirstNameChange}
              value={searchFirstName}
              name="pcpSearchFirstName"
            />

            <p className={styles.separator} />

            <Label>Last Name</Label>
            <InputText
              onChange={handleSearchLastNameChange}
              value={searchLastName}
              name="pcpSearchLastName"
            />
          </>
        ),
      },
      organization: {
        label: 'Organization',
        icon: (
          <BusinessOutlined
            aria-label="Organization"
            titleAccess="Organization"
            className="text-populate-green mt-0.5"
          />
        ),
        isReady: !isEmptyString(searchOrganizationName.trim())
          && !isEmptyString(searchStateAbbrev.trim()),
        searchDisabledReason: 'Enter an organization name and state to search',
        search: (input) => {
          if (isEmptyString(input.organizationName) || isEmptyString(input.state)) {
            setSearchValidationError('Enter an organization name and state to search.');
            setSubmittedSearchParams(null);
            return;
          }
          setSubmittedSearchParams({
            organizationName: input.organizationName,
            state: input.state,
            npiType: 'NPI-2 Organization',
          });
        },
        fields: (
          <>
            <Label>Organization Name</Label>
            <InputText
              onChange={handleSearchOrganizationNameChange}
              value={searchOrganizationName}
              name="pcpSearchOrganizationName"
              placeholder="e.g. Lakeside Family Medicine"
            />
          </>
        ),
      },
    }),
    [
      handleSearchFirstNameChange,
      handleSearchLastNameChange,
      handleSearchOrganizationNameChange,
      searchFirstName,
      searchLastName,
      searchOrganizationName,
      searchStateAbbrev,
    ],
  );

  const selectedProviderTypeConfig = providerTypeConfig[searchMode];
  const isSearchReady = selectedProviderTypeConfig.isReady;
  const isSearchDisabled = isSearchFetching || !isSearchReady;
  const searchDisabledReason = isSearchReady
    ? ''
    : selectedProviderTypeConfig.searchDisabledReason;

  const handleSearchClick = useCallback(() => {
    setSearchValidationError('');
    providerTypeConfig[searchMode].search({
      firstName: searchFirstName.trim(),
      lastName: searchLastName.trim(),
      organizationName: searchOrganizationName.trim(),
      state: searchStateAbbrev.trim(),
    });
  }, [
    providerTypeConfig,
    searchFirstName,
    searchLastName,
    searchMode,
    searchOrganizationName,
    searchStateAbbrev,
  ]);

  const handleNextClick = useCallback(async () => {
    if (!hasDispatchablePcpContext(pcpContext)) {
      await dispatch({
        type: actionPage,
        name: 'Primary Care Physician',
        value: {
          hasNoPrimaryCarePhysician: doesNotHavePrimaryCarePhysician,
        },
      });
      nextOrSubmitButton(nextPage);
      return;
    }

    await dispatch({
      type: actionPage,
      name: 'Primary Care Physician',
      value: {
        providerEntry: pcpContext.providerEntry,
        lastSeenOn: pcpContext.lastSeenOn ?? null,
        firstSeenOn: pcpContext.firstSeenOn ?? null,
        hasNoPrimaryCarePhysician: false,
      },
    });
    nextOrSubmitButton(nextPage);
  }, [
    actionPage,
    dispatch,
    doesNotHavePrimaryCarePhysician,
    nextOrSubmitButton,
    nextPage,
    pcpContext,
  ]);

  const selectedProviderType: ProviderType = pcpContext.providerEntry?.type === 'Organization'
    ? 'organization'
    : 'individual';

  const showNoSearchResultsMessage = shouldRunSearch && !isSearchFetching && !isSearchError
    && searchResults.length === 0;

  const hasSearchResults = searchResults.length > 0;
  const noSearchResultsText = `No ${searchMode === 'organization' ? 'organizations' : 'providers'} found. Try adjusting your search fields.`;

  const searchResultCards = useMemo(() => searchResults.map((item) => (
    <PhysicianSearchResultCard
      key={getNpiSearchResultKey(item)}
      item={item}
      onSelect={handleSelectSearchResult}
    />
  )), [searchResults, handleSelectSearchResult]);

  return (
    <>
      <MetaTag title="Primary Care Physician" />
      <Box pb={1}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Primary Care Physician
          </h2>
          <Stack spacing={2}>
            {showSearchForm && (
              <Box>
                <FormControlLabel
                  className="!flex"
                  control={(
                    <Checkbox
                      checked={doesNotHavePrimaryCarePhysician}
                      onChange={(event) => setDoesNotHavePrimaryCarePhysician(event.target.checked)}
                    />
                  )}
                  label="I do not have a Primary Care Physician"
                />
                {!doesNotHavePrimaryCarePhysician && (hasSearchResults ? (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: 2,
                        // mb: 2,
                      }}
                    >
                      <Typography variant="subtitle2" component="div">
                        Results
                      </Typography>
                      <Box
                        sx={{
                          flexShrink: 0,
                          marginLeft: 'auto',
                          '& .custom-button': {
                            width: 'auto',
                          },
                        }}
                      >
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleSearchAgain}
                          startIcon={<SearchOutlined sx={{ fontSize: '0.75rem' }} />}
                          sx={{ fontSize: '0.75rem', borderColor: 'grey.300' }}
                        >
                          Search Again
                        </Button>
                      </Box>
                    </Box>
                    <Box>{searchResultCards}</Box>
                  </>
                ) : (
                  <>
                    <Tabs
                      value={searchMode}
                      onChange={(_event, value: ProviderType) => (
                        handleSearchModeChange(value)
                      )}
                      aria-label="Primary care provider search type"
                      variant="fullWidth"
                      className="mb-3 !min-h-[42px] border-b border-gray-200 [&_.MuiTabs-indicator]:!bg-populate-green"
                    >
                      {Object.entries(providerTypeConfig).map(([value, config]) => (
                        <Tab
                          key={value}
                          value={value}
                          label={config.label}
                          icon={cloneElement(config.icon, { className: '!text-base' })}
                          iconPosition="start"
                          className="!min-h-[42px] !px-2 !py-1 !text-sm !font-normal !normal-case !tracking-normal !text-[var(--secondary-text-color)] [&.Mui-selected]:!text-populate-green"
                        />
                      ))}
                    </Tabs>

                    {providerTypeConfig[searchMode].fields}

                    <p className={styles.separator} />

                    <Label required>State</Label>
                    <Autocomplete<USStateType>
                      options={US_STATES}
                      value={selectedSearchState}
                      onChange={onSearchStateChange}
                      getOptionLabel={getUsStateAutocompleteLabel}
                      renderInput={renderAutocompleteTextField}
                      id="pcp-search-state"
                      classes={{
                        root: clsx('!w-full rounded'),
                      }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Tooltip
                        title={searchDisabledReason}
                        disableHoverListener={!searchDisabledReason}
                      >
                        <span>
                          <Button
                            type="button"
                            onClick={handleSearchClick}
                            disabled={isSearchDisabled}
                          >
                            {searchButtonLabel}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                    {searchValidationError ? (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {searchValidationError}
                      </Typography>
                    ) : null}
                    {isSearchError ? (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {searchError instanceof Error ? searchError.message : 'Search failed.'}
                      </Typography>
                    ) : null}
                    {showNoSearchResultsMessage ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {noSearchResultsText}
                      </Typography>
                    ) : null}
                  </>
                ))}
              </Box>
            )}

            {showSelectedCard && pcpContext.providerEntry ? (
              <SelectedPhysicianCard
                entry={pcpContext.providerEntry}
                icon={providerTypeConfig[selectedProviderType].icon}
                onEdit={handleEdit}
                onRemove={handleRemove}
              />
            ) : null}

            {showDatePicker ? (
              <Stack>
                <Label>Date Last Seen</Label>
                <MuiDatePicker
                  onChange={handleLastSeenChange}
                  value={lastSeenPickerValue}
                  disableFuture
                />
              </Stack>
            ) : null}
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button
            onClick={handleNextClick}
          >
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default PrimaryCarePhysician;
